/**
 * Impersonation State Models
 *
 * Core data structures for managing user impersonation sessions. These models represent the reactive state consumed by
 * all Module Federation remotes.
 *
 * @packageDocumentation
 */

/**
 * Represents the current impersonation session state
 *
 * This state is managed by ImpersonationService and exposed via signals. All Module Federation remotes consume this
 * state to determine if impersonation is active.
 */
export interface ImpersonationState {
  /** Whether impersonation is currently active */
  readonly isActive: boolean;

  /** Original admin user information (who initiated impersonation) Null when not impersonating */
  readonly originalAdmin: ImpersonationUser | null;

  /** Target user being impersonated Null when not impersonating */
  readonly impersonatedUser: ImpersonationUser | null;

  /** Unique session identifier for this impersonation session Null when not impersonating */
  readonly sessionId: string | null;

  /**
   * Original admin token ID (oi_tkn_id claim) used as the encryption key Required for decrypting the stored admin token
   * when ending impersonation Null when not impersonating
   */
  readonly adminTokenId: string | null;

  /**
   * Impersonation token ID (oi_tkn_id claim from the impersonation JWT) Sent as X-Impersonation-Session-Id header on
   * every outbound request so the backend can identify and restrict impersonated requests Null when not impersonating
   */
  readonly impersonationTokenId: string | null;

  /** ISO 8601 timestamp when impersonation started Null when not impersonating */
  readonly startedAt: string | null;

  /** ISO 8601 timestamp when impersonation token expires Null when not impersonating */
  readonly expiresAt: string | null;
}

/**
 * User information for impersonation context
 *
 * Contains essential user identity and permission claims needed to apply impersonation correctly across the
 * application.
 */
export interface ImpersonationUser {
  /** User's unique identifier */
  readonly userId: string;

  /** User type used for role-specific navigation and route guards */
  readonly userTypeId?: number;

  /** User's display name */
  readonly userName: string;

  /** User's email address */
  readonly userEmail: string;

  /** Primary client ID the user belongs to */
  readonly clientId: string;

  /** User's role identifier */
  readonly roleId: string;

  /** Array of entitlement codes (e.g., "Assessment.View", "Template.Edit") */
  readonly entitlements: readonly string[];
}

/**
 * Initial/empty impersonation state
 *
 * Used as the default state when no impersonation is active. All properties are null/false to indicate inactive state.
 */
export const EMPTY_IMPERSONATION_STATE: ImpersonationState = {
  isActive: false,
  originalAdmin: null,
  impersonatedUser: null,
  sessionId: null,
  adminTokenId: null,
  impersonationTokenId: null,
  startedAt: null,
  expiresAt: null,
} as const;

/**
 * Module configuration for impersonation scope
 *
 * Defines which Module Federation apps should apply impersonation. Only modules listed in applyImpersonation will use
 * impersonated permissions.
 */
export interface ImpersonationModuleConfig {
  /** Modules that SHOULD apply impersonation (use impersonated user permissions) */
  readonly applyImpersonation: readonly string[];

  /** Modules that SHOULD NOT apply impersonation (use original admin permissions) */
  readonly ignoreImpersonation: readonly string[];
}

/**
 * Module configuration constant
 *
 * Per specification: Only web.assessment applies impersonation. web.admin, web.shell, web.template, and web.audit
 * ignore it.
 */
export const MODULE_CONFIG: ImpersonationModuleConfig = {
  applyImpersonation: ['web.assessment'],
  ignoreImpersonation: ['web.admin', 'web.shell', 'web.template', 'web.audit'],
} as const;

/** Storage keys for impersonation data */
export const STORAGE_KEYS = {
  /** Key for encrypted original admin token in localStorage */
  ENCRYPTED_ADMIN_TOKEN: 'hx_impersonation_original_token',

  /** Key for the admin token ID used to derive the decryption key */
  ADMIN_TOKEN_ID: 'hx_impersonation_admin_token_id',

  /** Key for the original admin user type restored when impersonation ends */
  ORIGINAL_USER_TYPE_ID: 'hx_impersonation_original_user_type_id',

  /** Key for the effective user type restored while impersonation is active */
  IMPERSONATED_USER_TYPE_ID: 'hx_impersonation_user_type_id',

  /** Key for impersonation state sync events */
  SYNC_EVENT_KEY: 'hx_impersonation_sync',
} as const;
